import React, { useState, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { formatRut, formatMoneyWithCurrency } from '../../../utils/formatter';
import { documentsDic } from '../../../utils/dataDictionary';
import { CurrencyIcon } from 'src/utils/currencyIcon';
import moment from 'moment';
import FilterTabs from '../../FilterTabs/FilterTabs';
import FactoringNotice from './FactoringNotice';
import AnnulmentNotice from './AnnulmentNotice';
import MatchesTab from './MatchesTab';
import NominasTab from './NominasTab';
import CommentsTab from './CommentsTab';
import ParametrizacionTab from './ParametrizacionTab';
import ExportActions from '../../export/ExportActions';
import { dt, getDocumentDetailTabs, translateDocumentSummary } from 'src/utils/documentsI18n';
import './TaxDocumentDetail.css';

const FeesDetail = ({ data }) => {
  const { i18n } = useTranslation('documents');
  const [activeTab, setActiveTab] = useState('detalles');
  const [showOtherTaxesDetails, setShowOtherTaxesDetails] = useState(false);
  const isReceived = data.meta.isReceived;
  const isCreditOrDebitNote = [56, 61, 111, 112, "56", "61", "111", "112"].includes(data.meta.documentCode);

  // Calculate other taxes total and rate
  const otherTaxesTotal = data.meta?.otherTaxes?.reduce((sum, tax) => sum + (tax.value || 0), 0) || 0;
  const otherTaxesRate = data.meta?.netAmount ? (otherTaxesTotal / data.meta.netAmount) * 100 : 0;

  const commentCount = data.meta?.innerComments?.length || 0;
  const tabs = useMemo(
    () => getDocumentDetailTabs(commentCount),
    [commentCount, i18n.language]
  );

  const renderTabContent = () => {
    switch (activeTab) {
      case 'detalles':
        return (
          <div className="document-content">
            {/* Document Stamp */}
            <div className="document-stamp">
              <div className="document-stamp-title">
                {translateDocumentSummary(data.meta?.summary) || documentsDic[parseInt(data.meta.documentCode)]?.fullName}
              </div>
              <div className="document-stamp-number">
                {dt('detail.docNumberPrefix')} {data.meta.documentId || data.meta.number || dt('detail.noInfo')}
              </div>
              <div className="document-stamp-status">
                <span className={`status-badge ${
                  data.meta.isPaid 
                    ? 'paid' 
                    : data.meta?.isValid === false 
                      ? 'invalid' 
                      : 'pending'
                }`} style={{
                  ...(data.meta?.isValid === false && {
                    background: 'linear-gradient(135deg, #fef3f3 0%, #fde8e8 100%)',
                    border: '1px solid #f5c6cb',
                    color: '#991b1b'
                  })
                }}>
                  {data.meta?.isValid === false 
                    ? (data?.documentType === "TAX_DOCUMENT" && data?.meta.summary != 'Boleta de Honorarios' ? dt('detail.status.claimed') : dt('detail.status.voided'))
                    : data.meta.isPaid 
                      ? dt('detail.status.paid')
                      : dt('detail.status.pendingPayment')
                  }
                </span>
              </div>
            </div>
            
            {/* Main Info Grid */}
            <div className="document-grid">
              <div>
                <div className="info-block">
                  <div className="info-label">{isReceived ? dt('detail.issuedBy') : dt('detail.issuedTo')}</div>
                  <div className="counterpart-name" style={{ fontWeight: 800 }}>
                    {isReceived ? data.meta.issuerName || dt('common.noCounterparty') : data.meta.receiverName || dt('common.noCounterparty')}
                  </div>
                  <div className="info-value">
                    <span className="secondary" style={{ fontWeight: 600 }}>
                      {formatRut(isReceived ? data.meta.issuerId : data.meta.receiverId) || "-"}
                    </span>
                  </div>
                </div>
                <div className="info-row">
                  <div className="info-block">
                    <div className="info-label">{dt('detail.issueDate')}</div>
                    <div className="info-value">
                      {moment(data.documentDate).format("DD/MM/YYYY")}
                    </div>
                  </div>
                  <div className="info-block">
                    <div className="info-label">{dt('detail.dueDate')}</div>
                    <div className="info-value">
                      {data.originalDueDate ? moment(data.originalDueDate).format("DD/MM/YYYY") : dt('detail.noInfo')}
                    </div>
                  </div>
                  <div className="info-block">
                    <div className="info-label">{dt('detail.paymentDate')}</div>
                    <div className="info-value">
                      {data?.meta?.isPaid === true && data.paymentDate ? moment(data.paymentDate.substr(0, 10)).format("DD/MM/YYYY") : dt('detail.noInfo')}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Totals Card */}
            <div className="totals-card">
              {/* Exchange information - show first if exists and originalCurrency is not CLP */}
              {data.meta?.exchange?.originalCurrency && data.meta.exchange.originalCurrency !== 'CLP' && (
                <>
                  <div className="totals-segment">
                    <div className="totals-label">{dt('detail.originalCurrencyAmount', { currency: data.meta.exchange.originalCurrency })}</div>
                    <div className="totals-value">
                      {formatMoneyWithCurrency(data.meta.exchange.originalAmount || 0, data.meta.exchange.originalCurrency)}
                    </div>
                  </div>
                  {data.meta.exchange.exchangeRate && (
                    <div className="totals-segment">
                      <div className="totals-label">{dt('detail.exchangeType')}</div>
                      <div className="totals-value">
                        {data.meta.exchange.exchangeRate}
                      </div>
                    </div>
                  )}
                </>
              )}
                <div className="totals-segment">
                  <div className="totals-label">{dt('detail.feeTotal')}</div>
                  <div className="totals-value">
                    {formatMoneyWithCurrency(
                      (data.meta.netAmount || 0) + (data.meta.taxAmount || 0), 
                      data.currencyType
                    )}
                  </div>
                </div>
              <div className="totals-segment">
                <div className="totals-label">{dt('detail.retention', { rate: (data.meta?.taxRate * 100).toFixed(1) })}</div>
                <div className="totals-value">
                  {formatMoneyWithCurrency(
                    data.meta?.taxAmount || 0, 
                    data.currencyType
                  )}
                </div>
              </div>
              {data.meta?.taxableAmount > 0 && (
                <div className="totals-segment">
                  <div className="totals-label">{dt('detail.total')}</div>
                  <div className="totals-value">
                    {formatMoneyWithCurrency(
                      data.meta.taxableAmount, 
                      data.currencyType
                    )}
                  </div>
                </div>
              )}
              {data.meta.nonTaxableAmount > 0 && (
                <div className="totals-segment">
                  <div className="totals-label">{dt('detail.exemptAmount')}</div>
                  <div className="totals-value">
                    {formatMoneyWithCurrency(
                      data.meta.nonTaxableAmount, 
                      data.currencyType
                    )}
                  </div>
                </div>
              )}
              {/* Show MONTO (CLP) if exchange exists */}
              {data.meta?.exchange?.originalCurrency && data.meta.exchange.originalCurrency !== 'CLP' && (
                <div className="totals-segment">
                  <div className="totals-label">{dt('detail.amountClp')}</div>
                  <div className="totals-value">
                    {formatMoneyWithCurrency(data.meta.exchange.localAmount || 0, 'CLP')}
                  </div>
                </div>
              )}
                {data.meta?.otherTaxes && data.meta.otherTaxes.length > 0 && (
                  <div className="totals-segment">
                    <div className="totals-label">{dt('detail.otherTaxes', { rate: otherTaxesRate.toFixed(1) })}</div>
                    <div className="totals-value">
                      {formatMoneyWithCurrency(
                        otherTaxesTotal, 
                        data.currencyType
                      )}
                      <div 
                        className="totals-detail-link"
                        onClick={() => setShowOtherTaxesDetails(!showOtherTaxesDetails)}
                      >
                        {dt('detail.seeMoreDetail')} {showOtherTaxesDetails ? '↑' : '↓'}
                      </div>
                    </div>
                    {showOtherTaxesDetails && (
                      <div className="other-taxes-details">
                        {data.meta.otherTaxes.map((tax, index) => {
                          // Calculate the base amount for tax rate calculation
                          const baseAmount = data.meta?.taxableAmount || data.meta?.netAmount || 0;
                          
                          // Calculate tax rate: if taxRate is 0, use tax.value / baseAmount
                          const calculatedTaxRate = tax.taxRate === 0 && baseAmount > 0 
                            ? (tax.value || 0) / baseAmount 
                            : tax.taxRate;
                          
                          return (
                            <div key={index} className="other-tax-item">
                              <div className="other-tax-name">{tax.name} ({(calculatedTaxRate * 100).toFixed(1)}%)</div>
                              <div className="other-tax-value">
                                {formatMoneyWithCurrency(
                                  tax.value || 0, 
                                  data.currencyType
                                )}
                              </div>
                            </div>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
            </div>
            

            {/* Export Actions */}
            <ExportActions
              documentData={data}
              exportType="rendiciones"
              pdfUrl={data.meta.download?.pdf?.url || data.meta.download?.html?.url}
              xmlUrl={data.meta.download?.xml?.url}
              showPdf={!!(data.meta.download?.pdf?.url || data.meta.download?.html?.url)}
              showXml={!!data.meta.download?.xml?.url}
              showExcel={true}
            />

            {/* Factoring Notice */}
            {data.meta?.factoring?.cedido && (
              <FactoringNotice
                issuerName={data.meta.factoring.issuerName}
                cesionDate={data.meta.factoring.cesionDate ? moment(data.meta.factoring.cesionDate).format("DD/MM/YYYY") : null}
                downloadUrl={data.meta.download?.factoring?.url}
                cesiones={data.meta.factoring.cesiones}
              />
            )}

            {/* Annulment Notice */}
            {data.meta?.isValid === false && (
              <AnnulmentNotice
                text={dt('detail.feeAnnulled')}
                downloadUrl={null}
              />
            )}

            {/* No Incluir Notice */}
            {data.meta?.state === "NO_INCLUIR" && (
              <AnnulmentNotice
                text={dt('detail.noIncludeNotice')}
                downloadUrl={null}
                variant="noIncluir"
              />
            )}
                    {/* Retention Info */}
                    {data.meta?.retentionHolder && (
              <div className="info-row">
                <div className="info-block">
                  <div className="info-value">
                    {data.meta.retentionHolder === 'receptor' 
                      ? dt('detail.retentionReceptor')
                      : data.meta.retentionHolder === 'emisor'
                      ? dt('detail.retentionIssuer')
                      : ''
                    }
                  </div>
                </div>
              </div>
            )}

            {/* Glosa Section */}
            <div className="glosa-section">
              <div className="info-label">{dt('detail.glosa')}</div>
              {(() => {
                const filteredGlosa = data.meta?.description?.filter(item => {
                  // Filter out rows where all relevant fields are null/empty
                  const hasDescription = item.description || item.name || item.item;
                  const hasUnitPrice = item.unit_price && item.unit_price !== 0;
                  const hasQuantity = item.quantity && item.quantity !== 0;
                  const hasTotalPrice = item.total_price && item.total_price !== 0;
                  
                  return hasDescription || hasUnitPrice || hasQuantity || hasTotalPrice;
                }) || [];

                return filteredGlosa.length > 0 ? (
                  <div className="glosa-table">
                    <div className="glosa-items">
                      {filteredGlosa.map((item, index) => (
                        <div key={index} className="glosa-item">
                          <div className="glosa-item-description">
                            {item.name?.trim() && item.description?.trim() ? (
                              <>
                                <div>{item.name.trim()}</div>
                                <div className="glosa-item-subdescription">{item.description.trim()}</div>
                              </>
                            ) : (
                              item.name?.trim() || item.description?.trim() || item.item || dt('common.noDescription')
                            )}
                          </div>
                          <div className="glosa-item-metrics">
                            <div className="glosa-metric">
                              <div className="glosa-metric-label">{dt('detail.unitPrice')}</div>
                              <div className="glosa-metric-value">
                                {formatMoneyWithCurrency(
                                  item.unit_price || 0, 
                                  data.currencyType,
                                  true,
                                  true
                                )}
                              </div>
                            </div>
                            <div className="glosa-metric">
                              <div className="glosa-metric-label">{dt('detail.quantity')}</div>
                              <div className="glosa-metric-value">
                                {item.quantity || 1}
                              </div>
                            </div>
                            <div className="glosa-metric">
                              <div className="glosa-metric-label">TOTAL</div>
                              <div className="glosa-metric-value">
                                {formatMoneyWithCurrency(
                                  item.total_price || (item.unit_price * (item.quantity || 1)) || 0, 
                                  data.currencyType,
                                  true,
                                  true
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="glosa-empty">
                    {dt('detail.noGlosa')}
                  </div>
                );
              })()}
            </div>

          </div>
        );
      case 'glosa':
        return (
          <div className="glosa-tab-content">
            <div className="glosa-table">
              {/* Sample Items - Replace with actual data when available */}
              <div className="glosa-items">
                <div className="glosa-item">
                  <div className="glosa-item-description">
                    Servicio de consultoría mensual - ajuste de parámetros financieros.
                  </div>
                  <div className="glosa-item-metrics">
                    <div className="glosa-metric">
                      <div className="glosa-metric-label">{dt('detail.unitPrice')}</div>
                      <div className="glosa-metric-value">$150.000</div>
                    </div>
                    <div className="glosa-metric">
                      <div className="glosa-metric-label">{dt('detail.quantity')}</div>
                      <div className="glosa-metric-value">1</div>
                    </div>
                    <div className="glosa-metric">
                      <div className="glosa-metric-label">TOTAL</div>
                      <div className="glosa-metric-value">$150.000</div>
                    </div>
                  </div>
                </div>
                
                <div className="glosa-item">
                  <div className="glosa-item-description">
                    Revisión de conciliaciones bancarias y correcciones menores.
                  </div>
                  <div className="glosa-item-metrics">
                    <div className="glosa-metric">
                      <div className="glosa-metric-label">{dt('detail.unitPrice')}</div>
                      <div className="glosa-metric-value">$80.000</div>
                    </div>
                    <div className="glosa-metric">
                      <div className="glosa-metric-label">{dt('detail.quantity')}</div>
                      <div className="glosa-metric-value">1</div>
                    </div>
                    <div className="glosa-metric">
                      <div className="glosa-metric-label">TOTAL</div>
                      <div className="glosa-metric-value">$80.000</div>
                    </div>
                  </div>
                </div>
                
                <div className="glosa-item">
                  <div className="glosa-item-description">
                    Generación de reportes personalizados para cliente Hollmann SpA.
                  </div>
                  <div className="glosa-item-metrics">
                    <div className="glosa-metric">
                      <div className="glosa-metric-label">{dt('detail.unitPrice')}</div>
                      <div className="glosa-metric-value">$200.000</div>
                    </div>
                    <div className="glosa-metric">
                      <div className="glosa-metric-label">{dt('detail.quantity')}</div>
                      <div className="glosa-metric-value">1</div>
                    </div>
                    <div className="glosa-metric">
                      <div className="glosa-metric-label">TOTAL</div>
                      <div className="glosa-metric-value">$200.000</div>
                    </div>
                  </div>
                </div>
                
                <div className="glosa-item">
                  <div className="glosa-item-description">
                    Soporte técnico y resolución de incidencias en el sistema.
                  </div>
                  <div className="glosa-item-metrics">
                    <div className="glosa-metric">
                      <div className="glosa-metric-label">{dt('detail.unitPrice')}</div>
                      <div className="glosa-metric-value">$120.000</div>
                    </div>
                    <div className="glosa-metric">
                      <div className="glosa-metric-label">{dt('detail.quantity')}</div>
                      <div className="glosa-metric-value">1</div>
                    </div>
                    <div className="glosa-metric">
                      <div className="glosa-metric-label">TOTAL</div>
                      <div className="glosa-metric-value">$120.000</div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        );
      case 'matches':
        return (
          <MatchesTab data={data} />
        );
      case 'nominas':
        return (
          <NominasTab data={data} />
        );
      case 'parametrizacion':
        return (
          <ParametrizacionTab data={data} />
        );
      case 'comentarios':
        return (
          <CommentsTab data={data} />
        );
      default:
        return null;
    }
  };

  return (
    <div className="document-detail-container">
      {/* Tabs */}
      <FilterTabs
        tabs={tabs}
        activeTab={activeTab}
        onTabChange={setActiveTab}
        className="document-filter-tabs"
      />

      {/* Tab Content */}
      <div className="tab-content">
        {renderTabContent()}
      </div>
    </div>
  );
};

export default FeesDetail; 